import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMediaQuery, useTheme } from '@mui/material';

import { CrbmApi } from 'api';
import type { Column, Row } from 'ui';
import {
  Button,
  Container,
  ContainerItem,
  Dialog,
  InputCheckBox,
  Table,
  TableContainer,
  ZeroState
} from 'ui';
import { Spinner, SubTitle, Text } from 'ui/atoms';
import { capitalizeWords } from 'util/strings.util';
import palette from '../../theme/palette';

interface LicensesByBusinessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (selectedLicense?: { licenseId: string; businessId: string }, licenseNotFound?: boolean) => void;
  onCancel: () => void;
  selectedBusiness: string;
}

interface BusinessLicensesTableData {
  id: number;
  businessId: string;
  licenseNumber: string;
  licenseId: string;
  licenseAuthority: string;
  licenseType: string;
  med_rec_classification: string;
  status: string;
}

export const LicensesByBusinessModal: React.FC<LicensesByBusinessModalProps> = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const { isOpen, onClose, onSubmit, onCancel, selectedBusiness } = props;

  const crbmApi = new CrbmApi();

  const [selectedBusinessLicense, setSelectedBusinessLicense] =
    useState<{ licenseId: string; businessId: string }>();
  const [businessLicenseNotFound, setBusinessLicenseNotFound] = useState<boolean>(false);
  const [availableBusinessLicenses, setAvailableBusinessLicenses] = useState<BusinessLicensesTableData[]>([]);
  const [loadingBusinessLicenses, setLoadingBusinessLicenses] = useState(false);

  const form = useForm();

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setLoadingBusinessLicenses(true);

    crbmApi
      .searchBusinessLicenses(selectedBusiness)
      .then((res) => {
        const licenses = res.map((l) => {
          return {
            id: l.id,
            businessId: selectedBusiness ? selectedBusiness : '',
            licenseNumber: l.license_identifier ?? '--',
            licenseId: l.id.toString() ?? '--',
            licenseAuthority: l.licensing_authority_name ?? '--',
            licenseType: l.reported_license_type_description ?? '--',
            status: l.uniform_license_status_description ?? '--',
            med_rec_classification: l.med_rec_classification ?? '--'
          };
        });
        setAvailableBusinessLicenses(licenses);
      })
      .finally(() => {
        setLoadingBusinessLicenses(false);
      });
  }, [isOpen]);

  const businessLicensesColumns: Column<BusinessLicensesTableData>[] = [
    {
      id: 'licenseNumber',
      label: 'License Number',
      widthPercent: 20,
      format: (row) => (row.licenseNumber === '' ? '--' : row.licenseNumber)
    },
    {
      id: 'licenseAuthority',
      label: 'License Authority',
      widthPercent: 20,
      format: (row) => (row.licenseAuthority === '' ? '--' : row.licenseAuthority)
    },
    {
      id: 'licenseType',
      label: 'License Type',
      widthPercent: 20,
      format: (row) => (row.licenseType === '' ? '--' : row.licenseType)
    },
    {
      id: 'med_rec_classification',
      label: 'Med/Rec',
      widthPercent: 20,
      format: (row) =>
        row.med_rec_classification === '' ? '--' : capitalizeWords(row.med_rec_classification)
    },
    {
      id: 'status',
      label: 'Status',
      widthPercent: 20,
      format: (row) => (row.status === '' ? '--' : row.status)
    }
  ];

  const businessLicensesRows: Row<BusinessLicensesTableData>[] = availableBusinessLicenses.map((business) => {
    return {
      id: business.id.toString(),
      disableSelect: businessLicenseNotFound,
      data: {
        ...business
      }
    };
  });

  return (
    <Dialog
      action={
        <Button
          color="primary"
          disabled={loadingBusinessLicenses || (!selectedBusinessLicense && !businessLicenseNotFound)}
          label="Continue"
          onClick={() => onSubmit(selectedBusinessLicense, businessLicenseNotFound)}
          style={{ minWidth: isMobile ? '100%' : '100px' }}
        />
      }
      cancelButtonText="Back"
      onCancel={onCancel}
      handleClose={onClose}
      height={isMobile ? '400px' : '730px'}
      isOpen={isOpen}
      title="Marijuana Licenses"
      width={isTablet ? '85vw' : '898px'}
    >
      {loadingBusinessLicenses ? (
        <Container column data-cy="loading-business-licenses-container" padding={0} width="100%">
          <ContainerItem justify="center" width="100%">
            <Spinner />
            <br />
            <Text
              sx={
                isMobile
                  ? {
                      fontSize: '14px',
                      fontWeight: '500',
                      lineHeight: '20px',
                      color: palette.background.disabled
                    }
                  : {}
              }
              content="Checking business license data..."
            />
            {isMobile ? (
              <Text
                sx={
                  isMobile
                    ? {
                        fontSize: '14px',
                        fontWeight: '500',
                        lineHeight: '20px',
                        color: palette.background.disabled
                      }
                    : {}
                }
                content="(This could take up to one minute.)"
              />
            ) : (
              <SubTitle content="(This could take up to one minute.)" />
            )}
          </ContainerItem>
        </Container>
      ) : (
        <Container column data-cy="verify-business-license-container" padding={0} width="100%">
          <ContainerItem justify="left" padding={isMobile ? '0' : '0 8px 16px 8px'} width="100%">
            <Text content="We’ve found the following matches, please choose your Marijuana License." />
          </ContainerItem>

          <ContainerItem padding="8px 0" width="100%">
            <TableContainer>
              <Table
                minWidth={590}
                clickable
                singleSelectable
                columns={businessLicensesColumns}
                defaultSort="licenseNumber"
                defaultSortDirection="asc"
                noDataComponent={() => (
                  <ZeroState
                    noBorder
                    titleFont="medium"
                    title="Oops, we weren't able to find your license with the business provided"
                    icon={'/img/empty-states/no-results.svg'}
                  />
                )}
                returnSelection={(selected) => {
                  const businessLicense = businessLicensesRows.find((bl) => bl.id === selected[0]);

                  if (businessLicense) {
                    setSelectedBusinessLicense({
                      licenseId: businessLicense.data.licenseId,
                      businessId: businessLicense.data.businessId
                    });
                  }
                }}
                rows={businessLicensesRows}
              />
            </TableContainer>
          </ContainerItem>

          <ContainerItem width="100%">
            <InputCheckBox
              {...form}
              label="I don’t see my marijuana license"
              name="businessLicenseNotFound"
              onChange={(e, checked) => {
                setBusinessLicenseNotFound(checked);
              }}
            />
          </ContainerItem>
        </Container>
      )}
    </Dialog>
  );
};
